import React, { useEffect, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { publicApi, adminApi } from '../../api/client';
import { toast } from '../../hooks/use-toast';
import { Loader2, Save } from 'lucide-react';
import { useSettingsContext } from '../../context/SettingsContext';

const AdminSettings = () => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const { refresh } = useSettingsContext();

  useEffect(() => { publicApi.getSettings().then((d) => setForm({ ...d, hero_images: d.hero_images || [] })); }, []);

  if (!form) return <div className="py-20 text-center"><Loader2 className="animate-spin inline text-[#304b71]" /></div>;

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const updList = (k) => (e) => setForm({ ...form, [k]: e.target.value.split('\n').map((x) => x.trim()).filter(Boolean) });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await adminApi.updateSettings(form); await refresh(); toast({ title: 'Settings saved' }); }
    catch { toast({ title: 'Save failed', variant: 'destructive' }); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <div className="mb-8"><h1 className="font-serif text-3xl text-[#304b71]">Site Settings</h1><p className="text-slate-500 text-sm mt-1">Global configuration for brand, contact info and hero content.</p></div>

      <form onSubmit={submit} className="bg-white rounded-2xl border border-slate-100 p-8 space-y-6 max-w-4xl">
        <div>
          <h3 className="font-serif text-xl text-[#304b71] mb-4">Brand</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div><Label>Brand name</Label><Input value={form.brand} onChange={upd('brand')} className="mt-1" /></div>
            <div><Label>Tagline</Label><Input value={form.tagline} onChange={upd('tagline')} className="mt-1" /></div>
          </div>
        </div>

        <div>
          <h3 className="font-serif text-xl text-[#304b71] mb-4">Contact</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div><Label>Phone (display)</Label><Input value={form.phone} onChange={upd('phone')} className="mt-1" /></div>
            <div><Label>Phone (raw / tel:)</Label><Input value={form.phone_raw} onChange={upd('phone_raw')} className="mt-1" placeholder="+201559285650" /></div>
            <div><Label>Email</Label><Input value={form.email} onChange={upd('email')} className="mt-1" /></div>
            <div><Label>Address</Label><Input value={form.address} onChange={upd('address')} className="mt-1" /></div>
          </div>
        </div>

        <div>
          <h3 className="font-serif text-xl text-[#304b71] mb-4">Hero Section</h3>
          <div className="grid gap-4">
            <div><Label>Hero Title</Label><Input value={form.hero_title} onChange={upd('hero_title')} className="mt-1" /></div>
            <div><Label>Hero Subtitle</Label><Textarea rows={2} value={form.hero_subtitle} onChange={upd('hero_subtitle')} className="mt-1" /></div>
            <div><Label>Hero Image URLs (one per line)</Label><Textarea rows={4} value={(form.hero_images || []).join('\n')} onChange={updList('hero_images')} className="mt-1 font-mono text-xs" /></div>
          </div>
        </div>

        <div>
          <h3 className="font-serif text-xl text-[#304b71] mb-4">Social Media</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div><Label>Facebook URL</Label><Input value={form.social_facebook} onChange={upd('social_facebook')} className="mt-1" /></div>
            <div><Label>Instagram URL</Label><Input value={form.social_instagram} onChange={upd('social_instagram')} className="mt-1" /></div>
            <div><Label>Twitter URL</Label><Input value={form.social_twitter} onChange={upd('social_twitter')} className="mt-1" /></div>
            <div><Label>YouTube URL</Label><Input value={form.social_youtube} onChange={upd('social_youtube')} className="mt-1" /></div>
          </div>
        </div>

        <div className="pt-4 border-t border-slate-100">
          <Button type="submit" disabled={saving} className="bg-[#304b71] hover:bg-[#6a8aa4] text-white">
            {saving ? <Loader2 className="animate-spin" size={16} /> : <><Save size={16} className="mr-2" /> Save Settings</>}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AdminSettings;
